import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUtensils } from 'react-icons/fa';
import { api } from '../services/api';
import { useProfileData } from '../hooks/useProfileData';
import { useSubscription } from '../context/SubscriptionContext';
import DailyMealBuilder from './mealplanner/DailyMealBuilder';
import WeeklyMealPlanGenerator from './mealplanner/WeeklyMealPlanGenerator';
import GroceryListGenerator from './mealplanner/GroceryListGenerator';
import NutritionDashboard from './mealplanner/NutritionDashboard';
import MealExplorer from './mealplanner/MealExplorer';
import { PersonalizationBadgeGroup } from './common/PersonalizationIndicator';
import PremiumFeatureGate from './premium/PremiumFeatureGate';
import NutrientGapCallout from './premium/NutrientGapCallout';
import './mealplanner/MealPlanner.css';

function MealPlanner({ profile: propProfile, mode }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { isPremium } = useSubscription();
  const userId = user?.userId || user?.id;
  
  // Use unified profile data hook - this provides aggregated profile data from ProfileContext
  const {
    profile: contextProfile,
    preferences: contextPreferences,
    activeFactors,
    loading: profileLoading
  } = useProfileData();
  
  // Use profile from context if available, otherwise fall back to prop
  const profile = contextProfile || propProfile;
  const userPreferences = contextPreferences;
  
  const [activeTab, setActiveTab] = useState('builder'); // builder, weekly, grocery, nutrition, explorer
  const [symptoms, setSymptoms] = useState([]);
  const [currentMealPlan, setCurrentMealPlan] = useState(null);
  const [pendingMealSelection, setPendingMealSelection] = useState(null);
  
  // Memoize the callback to prevent unnecessary re-renders
  const handleMealPlanChange = useCallback((mealPlanData) => {
    console.log('📋 Daily Builder meal plan changed:', mealPlanData);
    
    // Merge with existing meal plan instead of replacing
    setCurrentMealPlan(prev => {
      if (!prev || !prev.days) {
        return mealPlanData;
      }
      
      // Merge: combine daily builder meals with weekly plan
      const today = new Date().toISOString().split('T')[0];
      const todayDateStr = today;
      
      // Find if today already exists in the plan
      const existingDays = [...(prev.days || [])];
      const todayIndex = existingDays.findIndex(day => {
        const dayDate = day.date instanceof Date 
          ? day.date.toISOString().split('T')[0] 
          : (typeof day.date === 'string' ? day.date : null);
        return dayDate === todayDateStr;
      });
      
      // Get today's meals from daily builder
      const todayMeals = mealPlanData?.days?.[0] || {};
      
      if (todayIndex >= 0) {
        // Update existing today's entry with daily builder meals (they take priority)
        existingDays[todayIndex] = {
          ...existingDays[todayIndex],
          ...todayMeals,
          // Preserve date
          date: existingDays[todayIndex].date
        };
      } else {
        // Add today's meals to the plan
        existingDays.push({
          date: todayDateStr,
          ...todayMeals
        });
        // Sort by date
        existingDays.sort((a, b) => {
          const dateA = a.date instanceof Date ? a.date : new Date(a.date);
          const dateB = b.date instanceof Date ? b.date : new Date(b.date);
          return dateA - dateB;
        });
      }
      
      return {
        ...prev,
        days: existingDays
      };
    });
  }, []);
  
  // Load meal plan from localStorage on mount
  useEffect(() => {
    if (userId) {
      try {
        const saved = localStorage.getItem(`dailyMeals_${userId}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          const savedDate = parsed.date;
          const today = new Date().toISOString().split('T')[0];
          if (savedDate === today && parsed.meals) {
            // Check if we have any meals
            const hasMeals = Object.values(parsed.meals).some(m => m !== null);
            if (hasMeals) {
              const mealPlanData = {
                days: [{
                  date: today,
                  breakfast: parsed.meals.breakfast,
                  lunch: parsed.meals.lunch,
                  dinner: parsed.meals.dinner,
                  snack: parsed.meals.snack
                }]
              };
              console.log('📋 Loaded meal plan from localStorage:', mealPlanData);
              setCurrentMealPlan(mealPlanData);
            }
          }
        }
      } catch (error) {
        console.error('Error loading meal plan from localStorage:', error);
      }
    }
  }, [userId]);
  
  // Handle meal selection from explorer or weekly plan
  const handleMealSelectFromExplorer = useCallback((mealType, meal) => {
    console.log('🍽️ Meal selected:', { mealType, meal });
    setPendingMealSelection({ mealType, meal });
    // Switch to Daily Builder tab to show the added meal
    setActiveTab('builder');
  }, []);
  
  const loadSymptoms = useCallback(async () => {
    if (!userId) return;
    
    try {
      const symptomsResponse = await api.getRecentSymptoms(userId);
      if (symptomsResponse?.data) {
        setSymptoms(symptomsResponse.data);
      }
    } catch (error) {
      console.error('Error loading symptoms:', error);
    }
  }, [userId]);

  // Load recent symptoms (preferences and profile come from ProfileContext via useProfileData)
  useEffect(() => {
    if (userId) {
      loadSymptoms();
    }
  }, [userId, loadSymptoms]);
  
  // Refresh profile data when it changes in context
  useEffect(() => {
    if (!profileLoading && userPreferences) {
      // Profile data is loaded and available from context
      console.log('✅ MealPlanner: Profile data available from ProfileContext', {
        hasProfile: !!profile,
        hasPreferences: !!userPreferences,
        activeFactors: activeFactors.length
      });
    }
  }, [profileLoading, userPreferences, profile, activeFactors]);
  
  // Helper function to generate impact messages for active factors
  const getImpactMessage = (factor) => {
    const impactMessages = {
      allergies: 'All meals are allergen-free',
      dietary: 'Dietary restrictions respected',
      medical: 'Medical conditions considered in recommendations',
      symptoms: 'Gentle, easy-to-digest options',
      lifestyle: 'Meals support your lifestyle needs',
      preference: 'Meals match your preferences',
      pregnancy: 'Meals tailored to your pregnancy stage'
    };
    return impactMessages[factor.type] || 'Personalized for you';
  };
  
  const tabs = [
    { id: 'builder', label: 'Daily Builder', icon: '🍳', description: 'Build your day' },
    { id: 'weekly', label: 'Weekly Plan', icon: '📆', description: 'Generate week' },
    { id: 'grocery', label: 'Grocery List', icon: '🛍️', description: 'Smart shopping list' },
    { id: 'nutrition', label: 'Nutrition', icon: '💊', description: 'Track nutrients' },
    { id: 'explorer', label: 'Explore Meals', icon: '🔎', description: 'Browse meals' }
  ];
  
  return (
    <div className="meal-planner-container">
      {/* Header */}
      <div className="meal-planner-header">
        <div className="meal-planner-title-section">
          <motion.div
            className="meal-planner-icon"
            animate={{ rotate: [0, -10, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          >
            🍽️
          </motion.div>
          <div>
            <p className="meal-planner-subtitle">
              Personalized meal recommendations based on your pregnancy stage, symptoms, and preferences
            </p>
            {/* Personalization Indicators - Using activeFactors from useProfileData */}
            {activeFactors && activeFactors.length > 0 && (
              <PersonalizationBadgeGroup
                factors={activeFactors.map(factor => ({
                  value: factor.value,
                  label: factor.label,
                  impact: getImpactMessage(factor),
                  icon: factor.icon,
                  color: factor.color,
                  compact: true
                }))}
                title="Personalized for you"
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="meal-planner-tabs">
        {tabs.map((tab) => (
          <motion.button
            key={tab.id}
            className={`meal-planner-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="tab-icon">{tab.icon}</span>
            <div className="tab-text">
              <span className="tab-label">{tab.label}</span>
              <span className="tab-description">{tab.description}</span>
            </div>
          </motion.button>
        ))}
      </div>
      
      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'builder' && (
          <motion.div
            key="builder"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <DailyMealBuilder
              userId={userId}
              profile={profile}
              userPreferences={userPreferences}
              symptoms={symptoms}
              onMealPlanChange={handleMealPlanChange}
              pendingMealSelection={pendingMealSelection}
              onPendingMealProcessed={() => setPendingMealSelection(null)}
            />
          </motion.div>
        )}
        
        {activeTab === 'weekly' && (
          <motion.div
            key="weekly"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {!isPremium ? (
              <PremiumFeatureGate
                featureId="meal_planner_premium"
                title={t('premium.mealPlanner.title', 'Weekly meal plan & grocery list')}
                description={t('premium.mealPlanner.teaserDescription', 'Personalized weekly plan, auto grocery list, and nutrient gap insights. Never calorie-focused.')}
                icon={<FaUtensils />}
                variant="card"
              />
            ) : (
            <>
            {currentMealPlan?.healthProfile && (
              <NutrientGapCallout healthProfile={currentMealPlan.healthProfile} />
            )}
            <WeeklyMealPlanGenerator
              userId={userId}
              profile={profile}
              userPreferences={userPreferences}
              symptoms={symptoms}
              onNavigateToGrocery={setActiveTab}
              onMealPlanGenerated={(weeklyPlan) => {
                console.log('📅 Weekly plan generated:', weeklyPlan);
                
                // Merge weekly plan with existing meal plan (preserve daily builder meals)
                setCurrentMealPlan(prev => {
                  if (!prev || !prev.days) {
                    return weeklyPlan;
                  }
                  
                  // Get today's date
                  const today = new Date().toISOString().split('T')[0];
                  
                  // Find today's meals from daily builder (if any)
                  const todayMeals = prev.days.find(day => {
                    const dayDate = day.date instanceof Date 
                      ? day.date.toISOString().split('T')[0] 
                      : (typeof day.date === 'string' ? day.date : null);
                    return dayDate === today;
                  });
                  
                  // Start with weekly plan days
                  const mergedDays = [...(weeklyPlan?.days || [])];
                  
                  // If we have today's meals from daily builder, merge them (daily builder takes priority)
                  if (todayMeals) {
                    const todayIndex = mergedDays.findIndex(day => {
                      const dayDate = day.date instanceof Date 
                        ? day.date.toISOString().split('T')[0] 
                        : (typeof day.date === 'string' ? day.date : null);
                      return dayDate === today;
                    });
                    
                    if (todayIndex >= 0) {
                      // Merge: daily builder meals override weekly plan for today
                      mergedDays[todayIndex] = {
                        ...mergedDays[todayIndex],
                        ...todayMeals,
                        date: mergedDays[todayIndex].date
                      };
                    } else {
                      // Add today's meals if not in weekly plan
                      mergedDays.push(todayMeals);
                      mergedDays.sort((a, b) => {
                        const dateA = a.date instanceof Date ? a.date : new Date(a.date);
                        const dateB = b.date instanceof Date ? b.date : new Date(b.date);
                        return dateA - dateB;
                      });
                    }
                  }
                  
                  return {
                    ...weeklyPlan,
                    days: mergedDays
                  };
                });
              }}
              onMealSelect={handleMealSelectFromExplorer}
            />
            </>
            )}
          </motion.div>
        )}
        
        {activeTab === 'grocery' && (
          <motion.div
            key="grocery"
            className="grocery-tab-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {!isPremium ? (
              <PremiumFeatureGate
                featureId="meal_planner_premium"
                title={t('premium.mealPlanner.title', 'Weekly meal plan & grocery list')}
                description={t('premium.mealPlanner.teaserDescription', 'Personalized weekly plan, auto grocery list, and nutrient gap insights. Never calorie-focused.')}
                icon={<FaUtensils />}
                variant="card"
              />
            ) : (
            <>
            <div className="grocery-tab-header">
              <div className="grocery-tab-header-content">
                <motion.div
                  className="grocery-tab-icon-wrapper"
                  animate={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                >
                  <div className="grocery-tab-icon">🛒</div>
                </motion.div>
                <div className="grocery-tab-text">
                  <h3 className="grocery-tab-title">Smart Grocery List</h3>
                  <p className="grocery-tab-description">
                    Your grocery list is automatically generated from your meal plans. 
                    Items are organized by store sections for efficient shopping.
                  </p>
                  {!currentMealPlan && (
                    <div className="grocery-tab-hint">
                      <span className="hint-icon">💡</span>
                      <span className="hint-text">
                        Generate a meal plan first to see your grocery list here!
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <GroceryListGenerator
              key={`grocery-${currentMealPlan ? JSON.stringify(currentMealPlan).substring(0, 50) : 'empty'}`}
              userId={userId}
              mealPlan={currentMealPlan}
              onNavigateToTab={setActiveTab}
            />
            </>
            )}
          </motion.div>
        )}
        
        {activeTab === 'nutrition' && (
          <motion.div
            key="nutrition"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <NutritionDashboard
              userId={userId}
              profile={profile}
              mealPlan={currentMealPlan}
            />
          </motion.div>
        )}
        
        {activeTab === 'explorer' && (
          <motion.div
            key="explorer"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <MealExplorer
              userId={userId}
              profile={profile}
              userPreferences={userPreferences}
              symptoms={symptoms}
              onMealSelect={handleMealSelectFromExplorer}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default MealPlanner;
